import React, {useContext, useMemo} from 'react';
import {useFormContext, useWatch} from "react-hook-form";
import useFieldArrayClean from "../../../../../hooks/use-field-array-clean.js";
import {Card} from "../../../../../components/card/card.jsx";
import {BoFormContext} from "../context/bo-form-context.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaPlus, FaTrash} from "react-icons/fa";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import FormField from "../../../../../components/react-hook-form/form-field.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {BoKeys} from "../../resources/bo-keys.js";
import BoFormIndividualSelect from "./bo-form-individual-select.jsx";
import handleTypes from "../utils/handle-types.jsx";
import InputFile from "../../../../../components/input/input-file.jsx";

const defaultUnitValues = [
    "MILLIGRAM", "GRAM", "KILOGRAM", "MILLILITER", "LITER",
    "CENTIMETER", "METER", "PIECE", "UNITY",
];

const findObjectType = (objectTypes, type) => objectTypes?.find((item) => item?.name === type);

const toOption = (value) => ({ value, label: BoKeys[value] ?? value });

const uniqueOptions = (options = []) => {
    const map = new Map();
    options.forEach((option) => {
        if (!option?.value) return;
        map.set(option.value, option);
    });
    return [...map.values()];
};

const StolenItemRow = ({ item, index, remove, isSync, objectTypes, typeOptions, verifySyncUrl }) => {
    const { control, setValue, getValues,  } = useFormContext();

    const currentValues = useWatch({
        control,
        name: [
            `stolenItemList.${index}.type`,
            `stolenItemList.${index}.subtype`,
            `stolenItemList.${index}.imei`,
            `stolenItemList.${index}.unit`,
            `stolenItemList.${index}.attachs`
        ]
    });

    const [type, subtype, imei, unit, attachs] = currentValues;

    const allowRemove = isSync || !!item.__isNew;
    const typeConfig = findObjectType(objectTypes, type);
    const subTypes = typeConfig?.subTypes ?? [];

    const subtypeOptions = useMemo(() => {
        return uniqueOptions([
            ...subTypes.map((st) => ({ value: st.name, label: st.name })),
            ...(subtype && !subTypes.some((st) => st.name === subtype) ? [{ value: subtype, label: subtype }] : []),
        ]);
    }, [subTypes, subtype]);

    const unitOptions = useMemo(() => {
        if (!type && !unit) return [];

        const selectedSubtype = subTypes.find((st) => st.name === subtype);
        const subtypeUnits = selectedSubtype?.units ?? [];

        if (!subtype) {
            if (!subTypes.length && type) return [toOption("UNITY")];
            if (!unit) return [];
        }

        if (subtypeUnits.length) {
            return uniqueOptions([
                ...subtypeUnits.map(toOption),
                ...(unit && !subtypeUnits.includes(unit) ? [toOption(unit)] : []),
            ]);
        }

        const defOptions = defaultUnitValues.map(toOption);
        return uniqueOptions([
            ...defOptions,
            ...(unit && !defOptions.some((opt) => opt.value === unit) ? [toOption(unit)] : []),
        ]);
    }, [type, subtype, unit, subTypes]);

    const currentTypeSelectOptions = useMemo(() => {
        return uniqueOptions([
            ...typeOptions,
            ...(type && !typeOptions.some((opt) => opt.value === type) ? [{ value: type, label: type }] : []),
        ]);
    }, [typeOptions, type]);

    const isPhone = type === "Celular";
    const disableSubtype = isPhone || !subtypeOptions.length;
    const disableUnit = !!type && !subtype && !subTypes.length;
    const disableQuantity = !!imei;
    const attachment = attachs?.[0];
    if (attachment && !attachment?.file) {
        attachment.url = `${verifySyncUrl(attachment.attachId)}/${attachment.attachId}`;
    }

    const addFile = (file) => {
        const attachId = Date.now();
        const newFile = Object.assign(file, { attachId });
        setValue(`stolenItemList.${index}.attachs`, [newFile]);
    };

    const removeFile = () => {
        setValue(`stolenItemList.${index}.attachs`, []);
    };

    return (
        <SentryRow className="p-4 border border-[#e5e7eb] rounded-md relative">
            {
                allowRemove &&
                    <Button
                        className="absolute -top-[10px] -right-[10px] !py-0 !px-0 !w-[25px] !h-[25px] z-10"
                        target="danger"
                        onClick={() => remove(index)}
                    >
                        <FaTrash />
                    </Button>
            }

            <BoFormIndividualSelect
                name={`stolenItemList.${index}.relatedPerson`}
                classNameWrapper="col-span-4"
            />

            <FormField
                name={`stolenItemList.${index}.type`}
                customRender={(field) => (
                    <SelectComponent
                        instanceId={`stolenItemList.${index}.type`}
                        value={currentTypeSelectOptions.find((opt) => opt.value === field.value) ?? null}
                        options={currentTypeSelectOptions}
                        label="Tipo *"
                        classNameWrapper="col-span-2"
                        onChange={(selected) => handleTypes.typeChange(selected, `stolenItemList.${index}`, setValue, objectTypes)}
                    />
                )}
            />

            {
                isPhone ?
                <FormField
                    name={`stolenItemList.${index}.imei`}
                    customRender={(field) => (
                        <InputForm
                            id={`stolenItemList.${index}.imei`}
                            name={field.name}
                            label="IMEI"
                            classNameWrapper="col-span-2"
                            value={field.value}
                            onChange={(event) => handleTypes.imeiChange(event, `stolenItemList.${index}`, setValue, getValues)}
                        />
                    )}
                />
                :
                <FormField
                    name={`stolenItemList.${index}.subtype`}
                    customRender={(field) => (
                        <SelectComponent
                            instanceId={`stolenItemList.${index}.subtype`}
                            value={subtypeOptions.find((opt) => opt.value === field.value) ?? null}
                            options={subtypeOptions}
                            label="Subtipo *"
                            classNameWrapper="col-span-2"
                            onChange={(selected) => handleTypes.subtypeChange(selected, `stolenItemList.${index}`, setValue, objectTypes, type)}
                            isDisabled={disableSubtype}
                        />
                    )}
                />
            }

            <FormField
                as={InputForm}
                name={`stolenItemList.${index}.quantity`}
                label="Quantidade *"
                classNameWrapper="col-span-2"
                type="number"
                min={0}
                disabled={disableQuantity}
            />

            <FormField
                name={`stolenItemList.${index}.unit`}
                customRender={(field) => (
                    <SelectComponent
                        instanceId={`stolenItemList.${index}.unit`}
                        value={unitOptions.find((opt) => opt.value === field.value) ?? null}
                        options={unitOptions}
                        label="Unidade *"
                        classNameWrapper="col-span-2"
                        onChange={(selected) => field.onChange(selected?.value ?? null)}
                        isDisabled={disableUnit}
                    />
                )}
            />

            <div className="col-span-2">
                <InputFile
                    file={attachment}
                    setFile={addFile}
                    removeFile={removeFile}
                    title="Anexar"
                    titleWithFile="Visualizar"
                    className="w-full"
                />
            </div>
        </SentryRow>
    );
};

const BoFormStolenItem = () => {
    const { control } = useFormContext();
    const { isSync, objectTypes, verifySyncUrl } = useContext(BoFormContext);

    const { fields, append, remove } = useFieldArrayClean({
        control,
        name: "stolenItemList"
    });

    const typeOptions = useMemo(
        () => (objectTypes ?? []).map((item) => ({ value: item.name, label: item.name })),
        [objectTypes]
    );

    return (
        <Card>
            <Card.Header>
                <Card.Title>Itens subtraídos</Card.Title>
            </Card.Header>

            <Card.Body className="space-y-4">
                {fields.map((item, index) => (
                    <StolenItemRow
                        key={item.id}
                        item={item}
                        index={index}
                        remove={remove}
                        isSync={isSync}
                        objectTypes={objectTypes}
                        typeOptions={typeOptions}
                        verifySyncUrl={verifySyncUrl}
                    />
                ))}

                <Button onClick={() => append({ itemId: fields?.at(-1) ? (fields?.at(-1).itemId + 1) : 1, __isNew: true })}>
                    <FaPlus /> Adicionar item subtraído
                </Button>
            </Card.Body>
        </Card>
    );
};

export default BoFormStolenItem;